/**
 * Elapsed-time formatting shared by the Active workout chooser and the
 * Health Today resume banner. Renders minutes ("23m") under an hour,
 * hours+minutes ("1h 05m") at or beyond an hour. Defensive against a null
 * `startTime` on an in_progress session (shouldn't happen, but the tracker
 * itself guards the same field — see WorkoutTracker.tsx).
 */
export function formatElapsed(startTime: string | null): string {
  if (!startTime) return '—';
  const ms = Date.now() - new Date(startTime).getTime();
  return formatMs(ms);
}

/**
 * Active elapsed (in ms) for a possibly-paused session (see
 * health-workout-pause design): `((pausedAt ?? now) − startTime) −
 * pausedTotalS`. While paused, the clock freezes at the pause instant;
 * running, it ticks from now. Epoch-ms arithmetic only (timezone-safe) and
 * clamped at 0 against clock skew. This is the SINGLE place the web client
 * computes active elapsed — the tracker calls it rather than re-deriving.
 * Returns null when there is no startTime (session not started).
 */
export function activeElapsedMs(
  startTime: string | null,
  pausedAt: string | null,
  pausedTotalS: number,
): number | null {
  if (!startTime) return null;
  const end = pausedAt ? new Date(pausedAt).getTime() : Date.now();
  return Math.max(0, end - new Date(startTime).getTime() - pausedTotalS * 1000);
}

/**
 * Human-readable active elapsed label ("23m", "1h 05m") for a possibly-paused
 * session. Thin wrapper over {@link activeElapsedMs} + {@link formatMs}.
 */
export function formatActiveElapsed(
  startTime: string | null,
  pausedAt: string | null,
  pausedTotalS: number,
): string {
  const ms = activeElapsedMs(startTime, pausedAt, pausedTotalS);
  if (ms === null) return '—';
  return formatMs(ms);
}

function formatMs(ms: number): string {
  const totalMin = Math.max(0, Math.round(ms / 60_000));
  if (totalMin < 60) return `${totalMin}m`;
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return `${h}h ${String(m).padStart(2, '0')}m`;
}
